import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import {
  fetchAssignableMembersForProgram,
  assignMemberToProgram,
  assignNewWorkerToProgram,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { COMPANY_ID } from "../../config/appConfig";

const memberClassLabel = (cls, t) => {
  switch (cls) {
    case "Collaborator":  return t("member.class.Collaborator");
    case "Specialist":    return t("member.class.Specialist");
    case "Investor":      return t("member.class.Investor");
    case "ProductUser":   return t("member.class.ProductUser");
    case "CompanyMember": return t("member.class.CompanyMember");
    default: return cls || "";
  }
};

const memberStateLabel = (state, t) => {
  switch (state) {
    case "Registered": return t("member.state.Registered");
    case "Assigned":   return t("member.state.Assigned");
    case "Released":   return t("member.state.Released");
    default: return state || "";
  }
};

export default function BuildProgramMemberAssignPage() {
  const { businessDomainId, businessUnitId, programId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const memberListPath = `/build/${businessDomainId}/business-units/${businessUnitId}/programs/${programId}/members`;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAssignableMembersForProgram(programId);
      setRows(
        (Array.isArray(data) ? data : []).map((m) => ({
          ...m,
          fullName: `${m.lastName || ""} ${m.firstName || ""}`.trim(),
          memberClassLabel: memberClassLabel(m.memberClass, t),
          memberStateLabel: memberStateLabel(m.memberState, t),
        }))
      );
    } catch (e) { notify.apiError(e); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAssign = async (member) => {
    setAssigning(true);
    try {
      // UI用付加フィールドを除外
      // eslint-disable-next-line no-unused-vars
      const { fullName, memberClassLabel: _cls, memberStateLabel: _st, ...memberData } = member;
      let res;
      if (memberData.memberCategoryId) {
        // 既存メンバー: PUT
        res = await assignMemberToProgram(programId, memberData);
      } else {
        // 新規ワーカー: POST
        res = await assignNewWorkerToProgram(programId, COMPANY_ID, memberData);
      }
      if (res?.result !== "OK") {
        throw new Error(res?.errorMessage || "assignment_failed");
      }
      notify.success(t("message.assign.success"));
      navigate(memberListPath, { replace: true });
    } catch (e) { notify.apiError(e); }
    finally { setAssigning(false); }
  };

  const columns = [
    { field: "fullName",         headerName: t("member.name"),  flex: 1, minWidth: 160 },
    { field: "memberClassLabel", headerName: t("member.class"), flex: 1, minWidth: 120 },
    { field: "memberStateLabel", headerName: t("member.state"), flex: 1, minWidth: 120 },
    {
      field: "_actions",
      headerName: "",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="contained"
          disabled={assigning}
          onClick={(e) => { e.stopPropagation(); handleAssign(params.row); }}
        >
          {t("program.member.assign.title")}
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("program.member.assign.title")}</Typography>
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(r) => r.workerId ?? r.memberId}
        loading={loading}
        autoHeight
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        disableRowSelectionOnClick
        sx={{ "& .MuiDataGrid-row": { cursor: "default" } }}
      />
      <Box sx={{ mt: 2 }}>
        <Button variant="outlined" onClick={() => navigate(`/build/${businessDomainId}/business-units/${businessUnitId}/programs/${programId}/members`)}>{t("common.back")}</Button>
      </Box>
    </Box>
  );
}
